import { propEq, propOr } from 'ramda'; // Only import the needed functions
import {
  IPolicy,
  IFamilyUserMap,
  PolicyArrangementType,
} from 'types';
import { PolicySort } from 'constants/options';
import {
  compareStringsCaseInsensitive,
  createdAtDescFn,
  sortByPolicyPriorityDesc,
} from 'helpers';

// Utility function to check window width
const isWindowWidthLessThan = (width) => window.innerWidth <= width;

// Encapsulate global access in utility functions
const getOnboardingPopupPosition = (step, breakpoint) => {
  switch (step) {
    case 1:
      return breakpoint === 'xs' && isWindowWidthLessThan(420)
        ? 'top center'
        : 'top right';
    case 3:
      return ['md', 'sm', 'xs', 'lg'].includes(breakpoint)
        ? 'top center'
        : 'left center';
    case 4:
      if (isWindowWidthLessThan(420)) return 'top left';
      return ['sm', 'xs'].includes(breakpoint) && isWindowWidthLessThan(520)
        ? 'top center'
        : 'top right';
    default:
      return 'left center';
  }
};

// Family options utility function
const getFamilyOptionsText = ({ name, familyOwner }) =>
  `${familyOwner ? 'My' : `${name}'s`} policies`;

export const getFamilyOptions = (familyUserMaps: IFamilyUserMap[]) =>
  familyUserMaps.map((f) => ({
    key: f.id,
    text: getFamilyOptionsText(f),
    value: f.id,
  }));

// Policies comparator
const policiesComparator = (key: string) => (a: IPolicy, b: IPolicy) =>
  compareStringsCaseInsensitive(a[key], b[key]);

// Use memoization for sorting function creation
const getSortFunction = (sortKey) => {
  const sortKeyMap = {
    [PolicySort.ALPHABETICAL]: 'policyType',
    [PolicySort.DATE_ADDED]: 'createdAt',
    [PolicySort.RENEWING_NEXT]: 'renewalDate',
  };

  return sortKey === PolicySort.DEFAULT
    ? (a, b) => sortByPolicyPriorityDesc(a.policyType, b.policyType)
    : policiesComparator(sortKeyMap[sortKey]);
};

// Sort policies with memoization
export const sortPolicies = (policies, sortKey) => {
  const sortFn = getSortFunction(sortKey);
  return policies.sort((a, b) => sortFn(a, b) || createdAtDescFn(a, b));
};

// Get employer tooltip
export const getEmployerTooltip = (employer) => {
  if (!employer) return '';

  const { name, policies = [] } = employer;
  const freePolicy = policies.find(
    propEq('arrangementType', PolicyArrangementType.FREE),
  );
  return propOr(name, 'policyTypeName', freePolicy);
};

